import React from "react"
import styled from "styled-components/macro"
import colors from "../../scripts/colors"
import { Link } from "gatsby"

type ButtonPropTypes = {
  icon: HTMLElement
  size?: "lg" | "m" | "sm"
  side?: "rt" | "lt"
  element?: string
  to?: string
  type?: string
  id?: string
  onClick?: any
  className?: string
  href?: string
  target?: string
  download?: boolean | string
}

const BtnWrap = styled.button`
  display: flex;
  align-items: center;
  font-family: "Noto Sans JP", sans-serif;
  border: none;
  background: none;
  cursor: pointer;
  text-decoration: none;
  position: relative;
  padding: 0;
  &,
  .Button-content,
  .Button-icon {
    transition: all 0.25s;
  }
  &:hover {
    .Button-content {
      background-color: ${colors("green", "lnkhlt")};
    }
    .Button-icon {
      background-color: ${colors("green", "dkhlt")};
    }
  }
`

const BtnContentBox = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 0;
  font-weight: 400;
  z-index: 100;
  color: white;
  justify-content: ${(props: any) =>
    props.side === "rt" ? "flex-start" : "flex-end"};
  background-color: ${colors("green", "lnk")};
  font-size: ${(props: any) => (props.size === "sm" ? 17 : 25)}px;
  height: calc(${(props: any) => (props.size === "sm" ? 36 : 52)}px - 0.15em);
  padding: 0
    ${(props: any) => {
      if (props.side === "rt") {
        return props.size === "sm" ? 15 : 35
      } else {
        return props.size === "sm" ? 10 : 17
      }
    }}px
    0.15em
    ${(props: any) => {
      if (props.side === "rt") {
        return props.size === "sm" ? 10 : 17
      } else {
        return props.size === "sm" ? 15 : 35
      }
    }}px;
  @media screen and (min-width: 1024px) {
    font-size: ${(props: any) =>
      props.size === "lg" ? 27 : props.size === "m" ? 25 : 17}px;
    height: calc(
      ${(props: any) =>
          props.size === "lg" ? 62 : props.size === "m" ? 52 : 36}px - 0.15em
    );
    padding: 0
      ${(props: any) => {
        if (props.side === "rt") {
          return props.size === "lg" ? 35 : props.size === "m" ? 35 : 15
        } else {
          return props.size === "lg" ? 17 : props.size === "m" ? 17 : 10
        }
      }}px
      0.15em
      ${(props: any) => {
        if (props.side === "rt") {
          return props.size === "lg" ? 17 : props.size === "m" ? 17 : 10
        } else {
          return props.size === "lg" ? 35 : props.size === "m" ? 35 : 15
        }
      }}px;
  }
`

export const BtnIconBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 200;
  background-color: ${colors("green", "dk")};
  border-radius: 50%;
  transition: all 0.25s;
  width: ${(props: any) => (props.size === "sm" ? 42 : 60)}px;
  height: ${(props: any) => (props.size === "sm" ? 42 : 60)}px;
  margin-right: ${(props: any) => {
    if (props.side === "rt") {
      return props.size === "sm" ? 3 : -7
    } else {
      return props.size === "sm" ? -7 : -17
    }
  }}px;
  margin-left: ${(props: any) => {
    if (props.side === "rt") {
      return props.size === "sm" ? -7 : -17
    } else {
      return props.size === "m" ? 3 : -7
    }
  }}px;
  border: ${(props: any) => (props.size === "sm" ? 3 : 5)}px solid white;
  svg {
    color: white;
    height: ${(props: any) => (props.size === "sm" ? 24 : 30)}px;
    width: ${(props: any) =>
      props.size === "lg" ? 38 : props.size === "sm" ? 24 : 30}px;
  }
  @media screen and (min-width: 1024px) {
    width: ${(props: any) =>
      props.size === "lg" ? 73 : props.size === "m" ? 60 : 42}px;
    height: ${(props: any) =>
      props.size === "lg" ? 73 : props.size === "m" ? 60 : 42}px;
    margin-right: ${(props: any) => {
      if (props.side === "rt") {
        return props.size === "lg" ? -7 : props.size === "m" ? -7 : 3
      } else {
        return props.size === "lg" ? -17 : props.size === "m" ? -17 : -7
      }
    }}px;
    margin-left: ${(props: any) => {
      if (props.side === "rt") {
        return props.size === "lg" ? -17 : props.size === "m" ? -17 : -7
      } else {
        return props.size === "lg" ? -7 : props.size === "m" ? -7 : 3
      }
    }}px;
    background-color: ${colors("green", "dk")};
    border-radius: 50%;
    border: ${(props: any) =>
        props.size === "lg" ? 7 : props.size === "m" ? 5 : 3}px
      solid white;
    transition: all 0.25s;
    svg {
      color: white;
      height: ${(props: any) =>
        props.size === "lg" ? 38 : props.size === "m" ? 30 : 24}px;
      width: ${(props: any) =>
        props.size === "lg" ? 38 : props.size === "m" ? 30 : 24}px;
    }
  }
`

const Button: React.FC<ButtonPropTypes> = ({
  children,
  className,
  icon,
  size = "m",
  side = "rt",
  element = "button",
  to,
  type,
  id,
  onClick,
  href,
  target,
  download,
  ...props
}) => {
  return (
    <BtnWrap
      className={`Button ${className ? className : ""}`}
      as={element === Link ? Link : element}
      to={to}
      type={type}
      id={id}
      onClick={onClick}
      href={href}
      target={target}
      download={download}
      props={props}
    >
      {side === "lt" && (
        <BtnIconBox size={size} side={side} className="Button-icon">
          {icon}
        </BtnIconBox>
      )}
      <BtnContentBox size={size} side={side} className="Button-content">
        {children}
      </BtnContentBox>
      {side === "rt" && (
        <BtnIconBox size={size} side={side} className="Button-icon">
          {icon}
        </BtnIconBox>
      )}
    </BtnWrap>
  )
}

export default Button
